import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { BUSINESS, IMAGES, img } from "../data/content";
import { scrollToId } from "../utils/scroll";

const LINES = ["Slow coffee.", "Warm plates.", "Long conversations."];

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={reduce ? false : { y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onReserve }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="section-home"
      className="relative flex min-h-[100svh] items-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={reduce ? {} : { y: bgY, scale: bgScale }}>
        <motion.img
          src={img(IMAGES.hero, 1800)}
          alt="Warm artisanal café diner interior with soft golden lighting"
          className="h-full w-full object-cover"
          initial={reduce ? false : { scale: 1.12, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#141210] via-[#141210]/55 to-[#141210]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#141210]/70 via-transparent to-transparent" />

      <motion.div
        style={reduce ? {} : { opacity: contentOpacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8 md:pb-32"
      >
        <motion.p
          className="eyebrow mb-6"
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          {BUSINESS.descriptor}
        </motion.p>
        <h1 className="font-display max-w-4xl text-[clamp(2.6rem,8vw,5.75rem)] font-semibold leading-[1.02] tracking-tight text-[#f7f4ef]">
          {LINES.map((line, i) => (
            <MaskedLine key={line} delay={0.3 + i * 0.16} reduce={reduce}>
              {line}
            </MaskedLine>
          ))}
        </h1>
        <motion.p
          className="mt-7 max-w-xl text-base leading-relaxed text-[#d8d2c9] md:text-lg"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
        >
          Roma's Café Diner sits above the hum of Lanka, near Ravidas Gate — a vintage-hearted room
          for honest coffee, diner-style plates and evenings that stretch a little longer.
        </motion.p>
        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
        >
          <button
            type="button"
            className="btn-gold"
            data-testid="hero-reserve-button"
            onClick={onReserve}
          >
            Reserve a Table
          </button>
          <button
            type="button"
            className="btn-outline"
            data-testid="hero-menu-button"
            onClick={() => scrollToId("menu")}
          >
            Explore the Menu
          </button>
          <a
            href={BUSINESS.phones[0].href}
            data-testid="hero-call-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#b5afa7] transition-colors hover:text-[#d4af37]"
          >
            <Phone size={15} /> {BUSINESS.phones[0].label}
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        onClick={() => scrollToId("story")}
        data-testid="hero-scroll-button"
        aria-label="Scroll to Our Story"
        className="absolute bottom-6 right-6 z-10 hidden rounded-full border border-[rgba(247,244,239,0.25)] p-3 text-[#f7f4ef] md:block"
        animate={reduce ? {} : { y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
};
